<div class="docs-container">
  <h1>📚 Car Wash Management System - Project Documentation</h1>

  <!-- 1. Project Overview -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 1. Project Overview</h2>
    
    <h3>What the project does</h3>
    <p>
      The Car Wash Management System is a comprehensive web application designed to manage all aspects of a car wash business. 
      It provides features for managing customers, employees, fleet vehicles, packages, orders, appointments, campaigns, 
      analytics, and website building. The system supports role-based access control with separate interfaces for 
      administrators and staff members.
    </p>

    <h3>Tech Stack</h3>
    <ul>
      <li><strong>Angular:</strong> Version 19.2.15</li>
      <li><strong>TypeScript:</strong> Version 5.7.2</li>
      <li><strong>SCSS:</strong> For styling</li>
      <li><strong>PrimeNG:</strong> Version 19.1.4 - UI component library</li>
      <li><strong>PrimeFlex:</strong> Version 4.0.0 - CSS utility library</li>
      <li><strong>RxJS:</strong> Version 7.8.0 - Reactive programming</li>
      <li><strong>Chart.js:</strong> Version 4.5.0 - Data visualization</li>
      <li><strong>FullCalendar:</strong> Version 6.1.19 - Calendar component</li>
      <li><strong>Quill:</strong> Version 1.3.7 - Rich text editor</li>
      <li><strong>Stripe:</strong> Payment processing (&#64;stripe/stripe-js, &#64;stripe/terminal-js)</li>
      <li><strong>Pusher:</strong> Real-time notifications</li>
      <li><strong>Amplitude:</strong> Analytics tracking</li>
      <li><strong>jsPDF:</strong> PDF generation</li>
      <li><strong>xlsx:</strong> Excel import/export</li>
      <li><strong>&#64;iplab/ngx-color-picker:</strong> Color picker for website builder</li>
      <li><strong>Rhombus:</strong> Camera/video stream integration (tunnel, public stream)</li>
    </ul>

    <h3>Architecture Summary</h3>
    <p>
      This application follows Angular's standalone component architecture (no NgModules). The project is organized into:
    </p>
    <ul>
      <li><strong>Feature Modules:</strong> Each major feature (campaign, orders, appointments, etc.) has its own directory</li>
      <li><strong>Shared Components:</strong> Reusable UI components in <code>/app/components</code> and <code>/app/shared/components</code></li>
      <li><strong>Services:</strong> Business logic and API communication in <code>/services</code></li>
      <li><strong>Guards:</strong> Route protection and authorization in <code>/guards</code></li>
      <li><strong>Models/Types:</strong> TypeScript interfaces and types in <code>/shared/types</code></li>
    </ul>
  </div>

  <!-- 2. Folder Structure Explanation -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 2. Folder Structure Explanation</h2>
    
    <div class="folder-structure">
/src <br>
  /app <br>
    /admin             # Super admin pages (login, dashboard, customers, campaigns) <br>
    /analytics         # Analytics dashboard and reports <br>
    /appointments      # Appointment management (admin & staff views) <br>
    /attendance        # Employee attendance tracking <br>
    /auth              # Authentication pages (login, forgot password, etc.) <br>
    /camera            # Camera views and public stream <br>
    /campaign          # Marketing campaign management <br>
    /car-wash-website  # Website builder and preview <br>
    /components        # Shared reusable components (NotificationModal, etc.) <br>
    /customer          # Customer management <br>
    /dashboard        # Main dashboard <br>
    /docs              # Project documentation (this page) <br>
    /employee          # Employee management <br>
    /fleet             # Fleet vehicle management <br>
    /guards            # Route guards (AuthGuard, RoleGuard, SuperAdminGuard, TunnelGuard) <br>
    /models            # Data models <br>
    /notifications     # Notifications list <br>
    /orders            # Order management (admin, staff, tunnel views) <br>
    /package           # Service package management <br>
    /profile           # User profile and settings <br>
    /rewards           # Customer rewards system <br>
    /sales             # Sales tracking and reports <br>
    /services          # Angular services (API, auth, etc.) <br>
    /shared            # Shared utilities, components, types <br>
    /tunnel            # Tunnel login and orders (kiosk-style interface) <br>
    /website-builder   # Website builder functionality <br>
  
  /assets <br>
    /icons             # SVG icons <br>
    /images            # Image assets (including LPR scan button) <br>
  
  /environments        # Environment configurations <br>
    environment.ts           # Development environment (default) <br>
    environment.prod.ts     # Production environment <br>
    environment.dev.ts      # Dev environment <br>
    environment.qa.ts       # QA environment <br>
    environment.staging.ts  # Staging environment <br>
    </div>

    <h3>Key Directories Explained</h3>
    <ul>
      <li><strong>/app/admin:</strong> Super admin interface (separate login, dashboard, customers, campaigns)</li>
      <li><strong>/app/camera:</strong> Camera views and public stream for LPR (License Plate Recognition)</li>
      <li><strong>/app/components:</strong> Shared UI components used across multiple features (e.g. NotificationModal)</li>
      <li><strong>/app/notifications:</strong> Notifications list and management</li>
      <li><strong>/app/services:</strong> Injectable services for API calls, authentication, state management</li>
      <li><strong>/app/shared:</strong> Shared utilities, types, interfaces, and common components (FloatingActionButton, AlertModal, etc.)</li>
      <li><strong>/app/guards:</strong> Route guards for protecting routes based on authentication and roles</li>
      <li><strong>/app/tunnel:</strong> Tunnel-specific login and order management for wash tunnel kiosks</li>
      <li><strong>/app/models:</strong> TypeScript interfaces and data models</li>
      <li><strong>/assets:</strong> Static assets like images, icons, fonts</li>
      <li><strong>/environments:</strong> Environment-specific configuration files</li>
    </ul>
  </div>

  <!-- 3. Angular Setup & Installation Guide -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 3. Angular Setup & Installation Guide</h2>
    
    <h3>Required Versions</h3>
    <ul>
      <li><strong>Node.js:</strong> Version 18.x or higher (recommended: LTS version)</li>
      <li><strong>Angular CLI:</strong> Version 19.2.15</li>
      <li><strong>npm:</strong> Version 9.x or higher</li>
    </ul>

    <h3>Installation Steps</h3>
    <ol>
      <li>
        <strong>Install Node.js:</strong> Download and install from <a href="https://nodejs.org/" target="_blank">nodejs.org</a>
      </li>
      <li>
        <strong>Install Angular CLI globally:</strong>
        <pre><code>npm install -g &#64;angular/cli&#64;19.2.15</code></pre>
      </li>
      <li>
        <strong>Clone the repository:</strong>
        <pre><code>git clone [repository-url]
cd web-carwash</code></pre>
      </li>
      <li>
        <strong>Install dependencies:</strong>
        <pre><code>npm install</code></pre>
      </li>
    </ol>

    <h3>Running the Project</h3>
    <ul>
      <li>
        <strong>Development server:</strong>
        <pre><code>ng serve</code></pre>
        or
        <pre><code>npm start</code></pre>
        This will start the development server on <code>http://localhost:4200</code>
      </li>
      <li>
        <strong>Open in browser automatically:</strong>
        <pre><code>ng serve --open</code></pre>
      </li>
      <li>
        <strong>Build for production:</strong>
        <pre><code>ng build --configuration production</code></pre>
      </li>
      <li>
        <strong>Run tests:</strong>
        <pre><code>ng test</code></pre>
      </li>
    </ul>
  </div>

  <!-- 4. Coding Guidelines -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 4. Coding Guidelines</h2>
    
    <h3>Naming Conventions</h3>
    <ul>
      <li><strong>Components:</strong> PascalCase with descriptive names (e.g., <code>CustomerComponent</code>, <code>OrderDetailsComponent</code>)</li>
      <li><strong>Services:</strong> PascalCase ending with "Service" (e.g., <code>ApiService</code>, <code>AuthService</code>)</li>
      <li><strong>Files:</strong> kebab-case matching component name (e.g., <code>customer.component.ts</code>)</li>
      <li><strong>Variables/Methods:</strong> camelCase (e.g., <code>getUserData()</code>, <code>isAuthenticated</code>)</li>
      <li><strong>Constants:</strong> UPPER_SNAKE_CASE (e.g., <code>API_BASE_URL</code>)</li>
      <li><strong>Interfaces/Types:</strong> PascalCase (e.g., <code>User</code>, <code>OrderStatus</code>)</li>
    </ul>

    <h3>SCSS Structure</h3>
    <ul>
      <li>Each component has its own SCSS file</li>
      <li>Global styles are in <code>src/styles.scss</code></li>
      <li>Use BEM methodology for CSS class naming when appropriate</li>
      <li>Leverage PrimeFlex utility classes for common styling needs</li>
      <li>Component styles are scoped by Angular's view encapsulation</li>
    </ul>

    <h3>Component Structure</h3>
    <p>All components follow this structure:</p>
    <pre><code>&#64;Component(&#123;
  selector: 'app-component-name',
  standalone: true,
  imports: [CommonModule, ...],
  templateUrl: './component-name.component.html',
  styleUrls: ['./component-name.component.scss']
&#125;)
export class ComponentNameComponent &#123;
  // Properties
  // Constructor
  // Lifecycle hooks
  // Methods
&#125;</code></pre>

    <h3>Writing Reusable Components</h3>
    <ul>
      <li>Use <code>&#64;Input()</code> for data passed into components</li>
      <li>Use <code>&#64;Output()</code> with <code>EventEmitter</code> for events</li>
      <li>Keep components focused on a single responsibility</li>
      <li>Extract complex logic into services</li>
      <li>Use standalone components (no NgModules)</li>
      <li>Document component inputs/outputs with JSDoc comments</li>
    </ul>

    <h3>Testing</h3>
    <ul>
      <li><strong>Framework:</strong> Jasmine with Karma (default Angular setup)</li>
      <li><strong>Run tests:</strong> <code>ng test</code></li>
      <li><strong>Spec files:</strong> Unit tests live alongside components/services as <code>*.spec.ts</code> (e.g. <code>component-name.component.spec.ts</code>)</li>
    </ul>

    <div class="note">
      <strong>Note:</strong> This project uses Angular's standalone component architecture. 
      All components are standalone and do not require NgModules.
    </div>
  </div>

  <!-- 5. Components Documentation -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 5. Components Documentation</h2>
    
    <h3>Shared Components</h3>
    <p>Key reusable components are in <code>/app/components</code> (e.g. NotificationModal) and <code>/app/shared/components</code> (e.g. FloatingActionButton, AlertModal):</p>

    <h4>NotificationModalComponent</h4>
    <ul>
      <li><strong>Location:</strong> <code>/app/components/notification-modal</code></li>
      <li><strong>Purpose:</strong> Displays notifications and handles order creation from notifications</li>
      <li><strong>Inputs:</strong> Receives notification data via ModalStateService</li>
      <li><strong>Outputs:</strong> Emits events for order creation and payment processing</li>
      <li><strong>Dependencies:</strong> ModalStateService, OrderCreationService, PaymentService, PackageService</li>
    </ul>

    <h4>FloatingActionButtonComponent</h4>
    <ul>
      <li><strong>Purpose:</strong> Global floating action button (FAB) for quick actions like LPR photo capture</li>
      <li><strong>Inputs:</strong> <code>actions</code> (FloatingAction[]), <code>isBusy</code> (boolean)</li>
      <li><strong>Outputs:</strong> <code>actionClick</code> - Emits when an action is clicked</li>
      <li><strong>Location:</strong> <code>/app/shared/components/floating-action-button</code></li>
    </ul>

    <h4>AlertModalComponent</h4>
    <ul>
      <li><strong>Purpose:</strong> Shared alert/confirmation modal for errors and messages</li>
      <li><strong>Inputs:</strong> <code>isOpen</code>, <code>title</code>, <code>description</code>, <code>additionalDescription</code>, <code>primaryButtonText</code>, <code>showCloseIcon</code></li>
      <li><strong>Outputs:</strong> <code>close</code>, <code>primaryAction</code></li>
      <li><strong>Usage:</strong> Used for LPR error messages, confirmation dialogs</li>
    </ul>

    <h4>Example Component Usage</h4>
    <pre><code>// In parent component template
&lt;app-notification-modal&gt;&lt;/app-notification-modal&gt;

// In parent component TypeScript
&#64;Input() user: User;
&#64;Output() updated = new EventEmitter&lt;User&gt;();

onUpdate() &#123;
  this.updated.emit(this.user);
&#125;</code></pre>

    <h3>Main Components</h3>
    <ul>
      <li><strong>DashboardComponent:</strong> Main dashboard with analytics and statistics</li>
      <li><strong>PackageComponent:</strong> Package management and CRUD operations</li>
      <li><strong>CustomerComponent:</strong> Customer management and CRUD operations</li>
      <li><strong>CampaignComponent:</strong> Marketing campaign management and analytics</li>
      <li><strong>AppointmentsComponent:</strong> Appointment scheduling and management</li>
      <li><strong>TunnelOrdersComponent:</strong> Tunnel/kiosk order management (TunnelGuard)</li>
      <li><strong>CameraComponent:</strong> Camera views and LPR integration</li>
      <li><strong>FleetComponent:</strong> Fleet vehicle management</li>
      <li><strong>AnalyticsComponent:</strong> Analytics dashboard and reports</li>
      <li><strong>SalesComponent:</strong> Sales tracking and reports</li>
      <li><strong>ProfileComponent:</strong> User profile and settings (also used for <code>/settings</code>)</li>
    </ul>

    <h3>App Root Features (AppComponent)</h3>
    <p>The root <code>AppComponent</code> hosts global features visible across the app (except on public routes like login, public-stream):</p>
    <ul>
      <li><strong>NotificationModal:</strong> Displays real-time notifications from Pusher; handles order creation, payment, and customer creation flows</li>
      <li><strong>FloatingActionButton (FAB):</strong> "Capture Photo" action for staff only—triggers camera/file picker for LPR (License Plate Recognition)</li>
      <li><strong>LPR Flow:</strong> Staff taps FAB → camera/file picker opens → image compressed via <code>compressImageForLpr</code> → <code>ApiService.uploadLicensePlateImage</code> → API returns notification type → <code>NotificationService.emitNotification</code> → modal shows result (order, create-customer, etc.)</li>
      <li><strong>AlertModal:</strong> Shows LPR errors (e.g. recognition failure, upload failure) with optional tips for better photos</li>
      <li><strong>Public routes:</strong> Modal and FAB are hidden on <code>/login</code>, <code>/forgot-password</code>, <code>/reset-password</code>, <code>/resend-email</code>, <code>/public-stream</code>, <code>/admin/login</code></li>
    </ul>
  </div>

  <!-- 6. Services & API Documentation -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 6. Services & API Documentation</h2>
    
    <h3>Core Services</h3>
    
    <h4>ApiService</h4>
    <p><strong>Purpose:</strong> Centralized HTTP client for all API requests</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>get&lt;T&gt;(endpoint: string, params?: any): Observable&lt;ApiResponse&lt;T&gt;&gt;</code> - Performs GET request with optional query parameters</li>
      <li><code>post&lt;T&gt;(endpoint: string, data: any): Observable&lt;ApiResponse&lt;T&gt;&gt;</code> - Performs POST request with request body</li>
      <li><code>put&lt;T&gt;(endpoint: string, data: any): Observable&lt;ApiResponse&lt;T&gt;&gt;</code> - Performs PUT request to update resources</li>
      <li><code>delete&lt;T&gt;(endpoint: string): Observable&lt;ApiResponse&lt;T&gt;&gt;</code> - Performs DELETE request to remove resources</li>
      <li><code>uploadLicensePlateImage(file: File): Observable&lt;ApiResponse&lt;any&gt;&gt;</code> - Uploads image for LPR (License Plate Recognition)</li>
    </ul>
    <p><strong>Base URL:</strong> Configured in environment files</p>
    <p><strong>Orders:</strong> Order list and details are requested via <code>ApiService</code> (GET/POST to orders endpoints). <code>OrderCreationService</code> handles the create-order-from-notification flow only.</p>

    <h4>ModalStateService</h4>
    <p><strong>Purpose:</strong> Manages notification modal visibility and pending notification data</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>showModal(notification: NotificationData): void</code> - Shows modal with notification; stores in localStorage if modal is already open</li>
      <li><code>hideModal(): void</code> - Hides modal and clears state</li>
    </ul>

    <h4>OrderCreationService</h4>
    <p><strong>Purpose:</strong> Handles order creation flow from notifications (create customer, create order, payment)</p>

    <h4>PaymentService</h4>
    <p><strong>Purpose:</strong> Payment processing (Stripe integration, payment requests)</p>

    <h4>RewardsService</h4>
    <p><strong>Purpose:</strong> Customer rewards program management</p>

    <h4>AttendanceService</h4>
    <p><strong>Purpose:</strong> Employee attendance tracking (clock in/out, schedules)</p>

    <h4>EmployeeService</h4>
    <p><strong>Purpose:</strong> Employee CRUD operations and management</p>

    <h4>RoleService</h4>
    <p><strong>Purpose:</strong> User role management; provides <code>getCurrentUserRole()</code>, <code>getDefaultRoute()</code>, <code>setUserRole()</code></p>

    <h4>AuthService</h4>
    <p><strong>Purpose:</strong> Authentication and user session management</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>login(credentials): Observable&lt;ApiResponse&gt;</code> - Authenticates user with email/password, handles force logout conflicts</li>
      <li><code>logout(): void</code> - Logs out user, clears session data and caches, redirects to login</li>
      <li><code>isAuthenticated(): boolean</code> - Checks if user is currently authenticated</li>
      <li><code>getCurrentUser(): User | null</code> - Returns current logged-in user data or null</li>
    </ul>

    <h4>CampaignService</h4>
    <p><strong>Purpose:</strong> Campaign management operations</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>getCampaigns(filters): Observable&lt;ApiResponse&lt;CampaignResponse&gt;&gt;</code> - Retrieves campaigns list with optional filters (platform, status, location, pagination)</li>
      <li><code>getCampaign(id): Observable&lt;ApiResponse&lt;Campaign&gt;&gt;</code> - Fetches single campaign details by ID</li>
      <li><code>createCampaign(data): Observable&lt;ApiResponse&gt;</code> - Creates a new marketing campaign</li>
      <li><code>updateCampaign(id, data): Observable&lt;ApiResponse&gt;</code> - Updates existing campaign details</li>
      <li><code>archiveCampaign(id, name): Observable&lt;ApiResponse&gt;</code> - Archives a campaign by setting status to archived</li>
      <li><code>publishCampaign(id): Observable&lt;ApiResponse&gt;</code> - Publishes a campaign to make it active</li>
    </ul>

    <h4>CustomerService</h4>
    <p><strong>Purpose:</strong> Customer CRUD operations and statistics</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>createCustomer(customerData, forceCreate?): Observable&lt;ApiResponse&gt;</code> - Creates new customer with optional force create flag</li>
      <li><code>getCustomers(params?): Observable&lt;ApiResponse&gt;</code> - Retrieves paginated customer list with filters (type, status, search, date range)</li>
      <li><code>getCustomer(customerId): Observable&lt;ApiResponse&gt;</code> - Fetches single customer details by ID</li>
      <li><code>updateCustomer(customerId, customerData): Observable&lt;ApiResponse&gt;</code> - Updates customer information</li>
      <li><code>updateCustomerStatus(customerId, status): Observable&lt;ApiResponse&gt;</code> - Updates customer status (active/inactive)</li>
      <li><code>deleteCustomer(customerId): Observable&lt;ApiResponse&gt;</code> - Deletes a customer record</li>
      <li><code>getCustomerProfile(customerId): Observable&lt;ApiResponse&gt;</code> - Retrieves complete customer profile with related data</li>
      <li><code>getCustomerStatistics(): Observable&lt;ApiResponse&gt;</code> - Fetches customer statistics and metrics</li>
    </ul>

    <h4>ProfileService</h4>
    <p><strong>Purpose:</strong> User profile and business profile management, location management</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>changePassword(data): Observable&lt;ApiResponse&gt;</code> - Updates user password with current and new password</li>
      <li><code>getBusinessProfile(): Observable&lt;ApiResponse&gt;</code> - Retrieves business/shop profile information</li>
      <li><code>updateBusinessProfile(shopId, data): Observable&lt;ApiResponse&gt;</code> - Updates business profile details (FormData supported)</li>
      <li><code>getLocations(packageId?): Observable&lt;ApiResponse&gt;</code> - Fetches locations list, optionally filtered by package ID</li>
      <li><code>loadLocations(packageId?): void</code> - Loads locations into service state and syncs with LocationService</li>
      <li><code>getLocationDetails(locationId): Observable&lt;ApiResponse&gt;</code> - Retrieves detailed information for a specific location</li>
      <li><code>updateLocationDetails(locationId, data): Observable&lt;ApiResponse&gt;</code> - Updates location details and refreshes cache</li>
      <li><code>deleteLocation(locationId): Observable&lt;ApiResponse&gt;</code> - Deletes a location and reloads locations list</li>
      <li><code>getUserProfile(): Observable&lt;ApiResponse&gt;</code> - Fetches current user profile data</li>
      <li><code>updateUserProfile(profileData): Observable&lt;ApiResponse&gt;</code> - Updates user profile and refreshes profile data</li>
      <li><code>setUserProfile(profile): void</code> - Sets user profile in service state (synchronous)</li>
      <li><code>refreshUserProfile(): void</code> - Refreshes user profile from API and updates state</li>
    </ul>
    <p><strong>Observables:</strong></p>
    <ul>
      <li><code>locations$: Observable&lt;any[]&gt;</code> - Stream of locations</li>
      <li><code>userProfile$: Observable&lt;UserProfile | null&gt;</code> - Stream of user profile</li>
    </ul>

    <h4>PackageService</h4>
    <p><strong>Purpose:</strong> Service package management with caching and state management</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>loadPackages(params?): void</code> - Loads packages into service state with optional filters</li>
      <li><code>addPackage(packageData): Observable&lt;ApiResponse&lt;Package&gt;&gt;</code> - Creates new service package and clears cache</li>
      <li><code>updatePackage(packageId, payload): Observable&lt;ApiResponse&lt;Package&gt;&gt;</code> - Updates package details and clears cache</li>
      <li><code>deletePackage(packageId): Observable&lt;ApiResponse&gt;</code> - Deletes a package and clears cache</li>
      <li><code>removePackageFromList(packageId): void</code> - Removes package from local state without API call</li>
      <li><code>getCarWashPackages(): Observable&lt;ApiResponse&lt;Package[]&gt;&gt;</code> - Fetches car wash packages with caching for orders</li>
      <li><code>getCarWashPackagesForStaff(sortBy?): Observable&lt;Package[]&gt;</code> - Gets car wash packages for staff/employees with optional sorting</li>
      <li><code>getDetailingPackages(): Observable&lt;ApiResponse&lt;Package[]&gt;&gt;</code> - Retrieves detailing service packages</li>
      <li><code>getPackagesByLocation(locationId): Observable&lt;Package[]&gt;</code> - Fetches packages available at a specific location</li>
      <li><code>clearPackagesCache(): void</code> - Clears all package caches and state</li>
      <li><code>clearCarWashPackagesCache(): void</code> - Clears only car wash packages cache</li>
    </ul>
    <p><strong>Observables:</strong></p>
    <ul>
      <li><code>packages$: Observable&lt;Package[]&gt;</code> - Stream of packages</li>
    </ul>

    <h4>LocationService</h4>
    <p><strong>Purpose:</strong> Location management with caching</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>getLocations(): Observable&lt;ApiResponse&gt;</code> - Fetches locations with 5-minute cache duration</li>
      <li><code>loadLocations(): void</code> - Loads locations into state if not already loaded</li>
      <li><code>clearLocationCache(): void</code> - Clears location cache and resets state</li>
      <li><code>getLocationOptions(): LocationOption[]</code> - Returns active locations formatted for dropdowns</li>
      <li><code>getLocationById(locationId): LocationApiResponse | undefined</code> - Finds location by ID from current state</li>
      <li><code>getCurrentLocations(): LocationApiResponse[]</code> - Returns current locations array from state</li>
      <li><code>refreshLocations(): void</code> - Forces refresh by clearing cache and reloading</li>
      <li><code>hasLocations(): boolean</code> - Checks if locations are currently loaded in state</li>
      <li><code>updateLocationsFromData(locations): void</code> - Updates state from external data source (syncs with ProfileService)</li>
    </ul>
    <p><strong>Observables:</strong></p>
    <ul>
      <li><code>locations$: Observable&lt;LocationApiResponse[]&gt;</code> - Stream of locations</li>
    </ul>

    <h4>AppointmentService</h4>
    <p><strong>Purpose:</strong> Appointment management and statistics</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>getAppointments(params): Observable&lt;ApiResponse&gt;</code> - Retrieves appointments list with filters (date, status, etc.)</li>
      <li><code>createAppointment(payload): Observable&lt;ApiResponse&gt;</code> - Creates new detailing appointment</li>
      <li><code>updateAppointment(appointmentId, payload): Observable&lt;ApiResponse&gt;</code> - Updates existing appointment details</li>
      <li><code>getTodaysAppointments(): Observable&lt;ApiResponse&gt;</code> - Fetches appointments scheduled for today</li>
      <li><code>getCompletedAppointments(): Observable&lt;ApiResponse&gt;</code> - Retrieves completed appointments</li>
      <li><code>getPendingAppointments(): Observable&lt;ApiResponse&gt;</code> - Gets pending appointments</li>
      <li><code>getAppointmentStats(): Observable&lt;ApiResponse&gt;</code> - Fetches appointment statistics (today's, completed, pending counts)</li>
      <li><code>cancelAppointment(appointmentId, payload): Observable&lt;ApiResponse&gt;</code> - Cancels an appointment with cancellation reason</li>
      <li><code>getAppointmentById(appointmentId): Observable&lt;ApiResponse&gt;</code> - Retrieves single appointment details by ID</li>
    </ul>

    <h4>NotificationService</h4>
    <p><strong>Purpose:</strong> Real-time notifications via Pusher integration</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>getConnectionStatus(): boolean</code> - Returns current Pusher connection status</li>
      <li><code>reconnect(): void</code> - Reconnects to Pusher channels if disconnected</li>
    </ul>
    <p><strong>Observables:</strong></p>
    <ul>
      <li><code>notification$: Observable&lt;NotificationData | null&gt;</code> - Stream of notifications</li>
    </ul>
    <p><strong>Supported Notification Types:</strong></p>
    <ul>
      <li><code>fleet-vehicle-found</code> - Fleet vehicle detected</li>
      <li><code>fleet-invoice-added</code> - Fleet invoice added</li>
      <li><code>invalid-license-plate</code> - Invalid license plate detected</li>
      <li><code>order-success</code> - Order completed successfully</li>
      <li><code>create-payment-request</code> - Payment request created</li>
      <li><code>create-customer-request</code> - Customer request created</li>
      <li><code>create-order-request</code> - Order request created</li>
      <li><code>order-not-found</code> - Order not found</li>
      <li><code>order-completed</code> - Order completed</li>
      <li><code>detailing-appointment-found</code> - Detailing appointment found</li>
      <li><code>vehicle-not-found</code> - Vehicle not found</li>
      <li><code>active-subscription</code> - Active subscription detected</li>
      <li><code>exit-active-subscription</code> - Exit active subscription</li>
      <li><code>exit-detailing-appointment-found</code> - Exit detailing appointment found</li>
    </ul>

    <h4>UserService</h4>
    <p><strong>Purpose:</strong> User management operations (Note: User management is primarily handled through ProfileService and AuthService)</p>
    <p><strong>Related Methods:</strong></p>
    <ul>
      <li>User profile: <code>ProfileService.getUserProfile()</code>, <code>ProfileService.updateUserProfile()</code></li>
      <li>Authentication: <code>AuthService.login()</code>, <code>AuthService.logout()</code>, <code>AuthService.getCurrentUser()</code></li>
      <li>Password: <code>ProfileService.changePassword()</code></li>
    </ul>

    <h4>VehicleService</h4>
    <p><strong>Purpose:</strong> Vehicle management operations</p>
    <p><strong>Methods:</strong></p>
    <ul>
      <li><code>getVehicleData(payload): Observable&lt;ApiResponse&lt;VehicleData&gt;&gt;</code> - Fetches vehicle information by license plate and state</li>
      <li><code>createVehicle(payload): Observable&lt;ApiResponse&gt;</code> - Creates new vehicle record with VIN, make, model, year</li>
      <li><code>getFamilyPlanVehicles(customerId): Observable&lt;ApiResponse&lt;FamilyPlanVehicle[]&gt;&gt;</code> - Retrieves vehicles associated with family plan membership</li>
      <li><code>getVehiclesByCustomerId(customerId): Observable&lt;ApiResponse&lt;any[]&gt;&gt;</code> - Gets all vehicles belonging to a customer</li>
      <li><code>updateVehicle(vehicleId, payload): Observable&lt;ApiResponse&gt;</code> - Updates vehicle details (license plate, VIN, make, model, year)</li>
      <li><code>deleteVehicle(vehicleId): Observable&lt;ApiResponse&gt;</code> - Deletes a vehicle record</li>
    </ul>

    <h4>Other Services</h4>
    <p>Additional services used across the app:</p>
    <ul>
      <li><strong>StripeService:</strong> Stripe SDK and terminal integration</li>
      <li><strong>PdfService:</strong> PDF generation (jsPDF)</li>
      <li><strong>AmplitudeService:</strong> Analytics tracking</li>
      <li><strong>NavigationService:</strong> Sidebar and navigation state</li>
      <li><strong>SalesService:</strong> Sales tracking and reports</li>
      <li><strong>LoggerService:</strong> Logging</li>
      <li><strong>CancellationService:</strong> Appointment/cancellation flows</li>
      <li><strong>LocationFormService:</strong> Location form handling</li>
      <li><strong>EmailPreviewService:</strong> Campaign email preview</li>
    </ul>
    <p>Fleet and order list/CRUD use <code>ApiService</code> with the appropriate API endpoints.</p>

    <h3>HTTP Interceptors</h3>
    <ul>
      <li><strong>AuthInterceptor</strong> (in <code>/app/services/auth.interceptor.ts</code>): Attaches the Bearer token to outgoing requests and handles 401 responses (logout and redirect to login).</li>
    </ul>

    <h3>API Request Format</h3>
    <p>All API requests follow this structure:</p>
    <pre><code>// GET Request
GET /api/endpoint?param1=value1&param2=value2
Headers: &#123;
  Authorization: 'Bearer &#123;token&#125;',
  Content-Type: 'application/json'
&#125;

// POST/PUT Request
POST /api/endpoint
Headers: &#123;
  Authorization: 'Bearer &#123;token&#125;',
  Content-Type: 'application/json'
&#125;
Body: &#123;
  // Request payload
&#125;</code></pre>

    <h3>API Response Format</h3>
    <pre><code>&#123;
  "success": boolean,
  "message": string,
  "data": T,  // Generic type based on endpoint
  "errors": [] // Optional, present on errors
&#125;</code></pre>

    <h3>Error Handling</h3>
    <ul>
      <li>HTTP errors are intercepted by <code>AuthInterceptor</code></li>
      <li>401 errors trigger automatic logout and redirect to login</li>
      <li>Global error handler (<code>GlobalErrorHandlerService</code>) logs errors</li>
      <li>User-friendly error messages displayed via PrimeNG MessageService</li>
    </ul>

    <h3>Security</h3>
    <ul>
      <li><strong>Tokens:</strong> JWT tokens are stored in localStorage (per login system: <code>userData</code>, <code>superAdminData</code>, <code>tunnelData</code>) and sent in the <code>Authorization: Bearer</code> header via AuthInterceptor</li>
      <li><strong>Secrets:</strong> Never commit API keys or secrets; use environment files and ensure sensitive keys are not exposed in client bundles</li>
    </ul>
  </div>

  <!-- 7. State Management -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 7. State Management</h2>
    
    <p>
      This application uses <strong>RxJS BehaviorSubject and Observables</strong> for state management 
      rather than NgRx, NGXS, or Akita. Services act as state containers using RxJS patterns.
    </p>

    <h3>State Management Pattern</h3>
    <ul>
      <li><strong>Services with BehaviorSubject:</strong> Services maintain state using BehaviorSubject</li>
      <li><strong>Observables:</strong> Components subscribe to service observables for reactive updates</li>
      <li><strong>Examples:</strong>
        <ul>
          <li><code>PackageService</code> - Manages package list state</li>
          <li><code>NavigationService</code> - Manages sidebar and navigation state</li>
          <li><code>ModalStateService</code> - Manages modal visibility state</li>
          <li><code>NotificationService</code> - Manages notification state</li>
        </ul>
      </li>
    </ul>

    <h3>Example Service State Pattern</h3>
    <pre><code>&#64;Injectable(&#123; providedIn: 'root' &#125;)
export class ExampleService &#123;
  private stateSubject = new BehaviorSubject&lt;StateType&gt;(initialValue);
  public state$ = this.stateSubject.asObservable();

  updateState(newValue: StateType) &#123;
    this.stateSubject.next(newValue);
  &#125;

  getCurrentState(): StateType &#123;
    return this.stateSubject.value;
  &#125;
&#125;</code></pre>
  </div>

  <!-- 8. Routing Documentation -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 8. Routing Documentation</h2>
    
    <h3>Authentication & Login Systems</h3>
    <p>The app has <strong>three separate login systems</strong>, each with its own login page, token storage, and role:</p>
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th>System</th>
            <th>Login Route</th>
            <th>Storage Key</th>
            <th>Role</th>
            <th>Guard</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Main App</strong> (Admin, Staff)</td>
            <td><code>/login</code></td>
            <td><code>userData</code></td>
            <td><code>admin</code>, <code>staff</code></td>
            <td>AuthGuard + RoleGuard</td>
          </tr>
          <tr>
            <td><strong>Super Admin</strong></td>
            <td><code>/admin/login</code></td>
            <td><code>superAdminData</code></td>
            <td><code>super_admin</code></td>
            <td>SuperAdminGuard</td>
          </tr>
          <tr>
            <td><strong>Tunnel</strong> (Kiosk)</td>
            <td><code>/tunnel/login</code></td>
            <td><code>tunnelData</code></td>
            <td><code>tunnel</code></td>
            <td>TunnelGuard</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p>Super Admin and Tunnel are <strong>independent</strong> from the main app: they use different login pages, different localStorage keys for tokens, and different guards. A user logged in as Super Admin cannot access main app or tunnel routes, and vice versa.</p>

    <h3>Route Structure</h3>
    <p>Routes are defined in <code>src/app/app.routes.ts</code></p>

    <h4>Public Routes (No Authentication Required)</h4>
    <ul>
      <li><code>/login</code> - Login page</li>
      <li><code>/forgot-password</code> - Password recovery</li>
      <li><code>/resend-email</code> - Resend verification email</li>
      <li><code>/reset-password</code> - Password reset</li>
      <li><code>/docs</code> - Project documentation (this page)</li>
      <li><code>/tunnel/login</code> - Tunnel kiosk login</li>
      <li><code>/admin/login</code> - Super admin login</li>
      <li><code>/website-preview/:id</code>, <code>/website-preview/:id/:pageType</code>, <code>/preview</code> - Website preview (public)</li>
      <li><code>/email-preview</code> - Email preview (public)</li>
      <li><code>/public-stream/:id</code> - Public camera stream (no auth)</li>
    </ul>

    <h4>Super Admin Routes</h4>
    <p>Requires: <code>SuperAdminGuard</code> (valid <code>superAdminData</code> in localStorage)</p>
    <ul>
      <li><code>/admin/dashboard</code> - Super admin dashboard</li>
      <li><code>/admin/customers</code> - Super admin customer management</li>
      <li><code>/admin/campaigns</code> - Super admin campaign management</li>
    </ul>

    <h4>Tunnel Routes</h4>
    <p>Requires: <code>TunnelGuard</code> (valid <code>tunnelData</code> in localStorage, role: tunnel)</p>
    <ul>
      <li><code>/tunnel</code> - Tunnel order management (kiosk interface)</li>
    </ul>

    <h4>Protected Routes (Admin Only)</h4>
    <p>Requires: <code>AuthGuard</code> + <code>RoleGuard</code> (Admin role)</p>
    <ul>
      <li><code>/dashboard</code> - Main dashboard</li>
      <li><code>/customer</code> - Customer management</li>
      <li><code>/package</code> - Package management</li>
      <li><code>/employee</code> - Employee management</li>
      <li><code>/fleet</code> - Fleet management</li>
      <li><code>/campaign</code> - Campaign management</li>
      <li><code>/campaign-editor</code> - Campaign editor</li>
      <li><code>/analytics</code> - Analytics dashboard</li>
      <li><code>/car-wash-website</code> - Website builder</li>
    </ul>

    <h4>Protected Routes (Staff Only)</h4>
    <ul>
      <li><code>/attendance</code> - Attendance tracking</li>
    </ul>

    <h4>Protected Routes (Admin & Staff)</h4>
    <ul>
      <li><code>/camera</code> - Camera views and LPR</li>
      <li><code>/appointments</code> - Appointment management</li>
      <li><code>/orders</code> - Order management</li>
      <li><code>/notifications</code> - Notifications list</li>
      <li><code>/rewards</code> - Rewards management</li>
      <li><code>/sales</code> - Sales tracking</li>
      <li><code>/profile</code> - User profile</li>
      <li><code>/settings</code> - User settings (ProfileComponent)</li>
    </ul>

    <h4>Other Routes</h4>
    <ul>
      <li><code>/website-builder</code> - Website builder (AuthGuard, RoleGuard)</li>
    </ul>

    <h3>Route Guards</h3>
    <ul>
      <li><strong>AuthGuard:</strong> Checks if user is authenticated, redirects to login if not</li>
      <li><strong>RoleGuard:</strong> Checks user role permissions, redirects based on role if unauthorized</li>
      <li><strong>SuperAdminGuard:</strong> Validates <code>superAdminData</code> in localStorage; redirects to <code>/admin/login</code> if invalid</li>
      <li><strong>TunnelGuard:</strong> Validates <code>tunnelData</code> in localStorage; redirects to <code>/tunnel/login</code> if invalid</li>
    </ul>

    <h3>User Roles</h3>
    <p>Defined in <code>/app/shared/types/role.interface.ts</code>:</p>
    <ul>
      <li><code>admin</code> - Administrator (full access to dashboard, customers, packages, etc.)</li>
      <li><code>staff</code> - Staff/Employee (attendance, orders, appointments, rewards, sales)</li>
      <li><code>super_admin</code> - Super Administrator (admin dashboard, customers, campaigns)</li>
      <li><code>tunnel</code> - Tunnel kiosk user (tunnel orders only)</li>
    </ul>

    <h3>Default Routes</h3>
    <ul>
      <li><code>/</code> - Redirects to <code>/login</code></li>
      <li><code>**</code> - Wildcard route, redirects to <code>/login</code></li>
    </ul>

    <div class="note">
      <strong>Note:</strong> All routes use standalone components. There are no lazy-loaded modules in this application.
    </div>
  </div>

  <!-- 9. Reusable UI Components / Design System -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 9. Reusable UI Components / Design System</h2>
    
    <p>This project uses <strong>PrimeNG</strong> as the primary UI component library.</p>

    <h3>Available PrimeNG Components</h3>
    <ul>
      <li><strong>Buttons:</strong> <code>p-button</code> - Various button styles and sizes</li>
      <li><strong>Forms:</strong> <code>p-inputText</code>, <code>p-inputNumber</code>, <code>p-textarea</code>, <code>p-dropdown</code>, <code>p-calendar</code></li>
      <li><strong>Modals:</strong> <code>p-dialog</code> - Modal dialogs</li>
      <li><strong>Tables:</strong> <code>p-table</code> - Data tables with sorting, filtering, pagination</li>
      <li><strong>Cards:</strong> <code>p-card</code> - Card components</li>
      <li><strong>Toast:</strong> <code>p-toast</code> - Toast notifications</li>
      <li><strong>Menus:</strong> <code>p-menu</code>, <code>p-menubar</code> - Navigation menus</li>
      <li><strong>Charts:</strong> Integration with Chart.js for data visualization</li>
    </ul>

    <h3>PrimeFlex Utility Classes</h3>
    <p>PrimeFlex provides utility classes for common styling:</p>
    <ul>
      <li><code>p-grid</code>, <code>p-col</code> - Grid system</li>
      <li><code>p-mt-*</code>, <code>p-mb-*</code>, <code>p-p-*</code> - Spacing utilities</li>
      <li><code>p-text-*</code> - Text alignment and styling</li>
      <li><code>p-flex</code> - Flexbox utilities</li>
    </ul>

    <h3>Theme Configuration</h3>
    <p>The application uses PrimeNG's Aura theme with dark mode support:</p>
    <pre><code>providePrimeNG(&#123;
  theme: &#123;
    preset: Aura,
    options: &#123;
      darkModeSelector: '.my-app-dark'
    &#125;
  &#125;,
  ripple: true,
  inputStyle: 'outlined'
&#125;)</code></pre>

    <h3>Typography</h3>
    <p>In the app, typography is driven by the PrimeNG theme. This documentation page uses: <code>-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif</code></p>

    <h3>Color Tokens</h3>
    <p>Colors are managed through PrimeNG theme system. Primary color: <span style="color: #3498db;">#3498db</span> (Blue)</p>

    <h3>Do's and Don'ts</h3>
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th>Do</th>
            <th>Don't</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Use PrimeNG components for consistency</td>
            <td>Create custom components that duplicate PrimeNG functionality</td>
          </tr>
          <tr>
            <td>Follow PrimeNG component API documentation</td>
            <td>Override PrimeNG styles unless necessary</td>
          </tr>
          <tr>
            <td>Use PrimeFlex utilities for layout</td>
            <td>Write custom CSS for common spacing/layout needs</td>
          </tr>
          <tr>
            <td>Maintain consistent button styles</td>
            <td>Mix different button styles unnecessarily</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- 10. Environment Configurations -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 10. Environment Configurations</h2>
    
    <h3>Environment Files</h3>
    <p>Located in <code>src/environments/</code></p>
    <ul>
      <li><code>environment.ts</code> - Development environment (default)</li>
      <li><code>environment.prod.ts</code> - Production environment</li>
      <li><code>environment.dev.ts</code> - Dev environment</li>
      <li><code>environment.qa.ts</code> - QA environment</li>
      <li><code>environment.staging.ts</code> - Staging environment</li>
    </ul>

    <h3>Environment Structure</h3>
    <pre><code>export const environment = &#123;
  production: boolean,
  baseUrl: string,           // API base URL
  imageUrl: string,          // Image storage URL (e.g. S3 bucket)
  stripePublicKey: string,   // Stripe public key (client-side)
  amplitudeApiKey: string,   // Amplitude analytics key
  rhombusApiKey?: string,   // Rhombus camera API key (optional)
&#125;;</code></pre>

    <h3>API Configuration</h3>
    <ul>
      <li><strong>Development:</strong> <code>https://carwashdev.way.com/api</code></li>
      <li><strong>Production:</strong> Configured in <code>environment.prod.ts</code></li>
    </ul>

    <h3>Build Configurations</h3>
    <p>Build configurations are defined in <code>angular.json</code>:</p>
    <ul>
      <li><strong>development:</strong> Default development build</li>
      <li><strong>production:</strong> Optimized production build with minification</li>
    </ul>

    <div class="warning">
      <strong>Warning:</strong> Never commit API keys or secrets to version control. 
      Use environment variables for sensitive data in production.
    </div>
  </div>

  <!-- 11. Build, Deploy & Environment Setup -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 11. Build, Deploy & Environment Setup</h2>
    
    <h3>Production Build</h3>
    <p>To generate a production build:</p>
    <pre><code>ng build --configuration production</code></pre>
    <p>Output directory: <code>dist/web-carwash/</code></p>

    <h3>Build Options</h3>
    <ul>
      <li><strong>Development build:</strong> <code>ng build</code> or <code>ng build --configuration development</code></li>
      <li><strong>Watch mode:</strong> <code>ng build --watch</code> - Rebuilds on file changes</li>
      <li><strong>Production build:</strong> <code>ng build --configuration production</code> - Optimized, minified</li>
    </ul>

    <h3>Deployment Steps</h3>
    <ol>
      <li>Build the application: <code>ng build --configuration production</code></li>
      <li>Test the build locally by serving the <code>dist/web-carwash</code> directory</li>
      <li>Upload the contents of <code>dist/web-carwash</code> to your hosting provider</li>
      <li>Configure the web server to serve <code>index.html</code> for all routes (SPA routing)</li>
      <li>Set up environment variables if needed</li>
    </ol>

    <h3>Common Hosting Platforms</h3>
    <ul>
      <li><strong>Firebase Hosting:</strong> Use <code>firebase deploy</code> after configuring Firebase</li>
      <li><strong>AWS S3 + CloudFront:</strong> Upload to S3 bucket, configure CloudFront distribution</li>
      <li><strong>Vercel:</strong> Connect repository, Vercel handles build and deployment</li>
      <li><strong>Netlify:</strong> Connect repository, configure build command and publish directory</li>
    </ul>

    <!-- <h3>CI/CD Process</h3>
    <p>If CI/CD is configured:</p>
    <ul>
      <li>Automated builds on push to main/master branch</li>
      <li>Automated tests before deployment</li>
      <li>Deployment to staging/production environments</li>
    </ul>
    <p><em>Note: Specific CI/CD configuration depends on your setup (GitHub Actions, GitLab CI, Jenkins, etc.)</em></p> -->

    <h3>Environment-Specific Builds</h3>
    <pre><code># Development
ng build --configuration development

# QA
ng build --configuration qa

# Staging
ng build --configuration staging

# Production
ng build --configuration production</code></pre>
  </div>

  <!-- 12. Common Issues & Troubleshooting -->
  <div class="section">
    <h2><span class="checkmark">✅</span> 12. Common Issues & Troubleshooting</h2>
    
    <h3>Build Errors</h3>
    <h4>Error: Module not found</h4>
    <ul>
      <li><strong>Solution:</strong> Run <code>npm install</code> to ensure all dependencies are installed</li>
      <li>Check if the import path is correct</li>
      <li>Verify the file exists in the expected location</li>
    </ul>

    <h4>Error: Type errors</h4>
    <ul>
      <li><strong>Solution:</strong> Check TypeScript version compatibility</li>
      <li>Ensure all types are properly imported</li>
      <li>Run <code>ng build</code> to see detailed error messages</li>
    </ul>

    <h3>Angular Version Issues</h3>
    <h4>Angular CLI version mismatch</h4>
    <ul>
      <li><strong>Solution:</strong> Ensure Angular CLI version matches Angular version</li>
      <li>Update globally: <code>npm install -g &#64;angular/cli&#64;19.2.15</code></li>
      <li>Check version: <code>ng version</code></li>
    </ul>

    <h4>Dependency conflicts</h4>
    <ul>
      <li><strong>Solution:</strong> Delete <code>node_modules</code> and <code>package-lock.json</code></li>
      <li>Run <code>npm install</code> to reinstall dependencies</li>
      <li>If issues persist, try <code>npm ci</code> for a clean install</li>
    </ul>

    <h3>Runtime Issues</h3>
    <h4>CORS errors</h4>
    <ul>
      <li><strong>Solution:</strong> Ensure API server has CORS enabled</li>
      <li>Check environment configuration for correct API URL</li>
      <li>Use Angular proxy for development (configure in <code>angular.json</code>)</li>
    </ul>

    <h4>Authentication issues</h4>
    <ul>
      <li><strong>Solution:</strong> Check if token is stored in localStorage</li>
      <li>Verify AuthInterceptor is properly configured</li>
      <li>Check API response format matches expected structure</li>
    </ul>

    <h4>PrimeNG component not rendering</h4>
    <ul>
      <li><strong>Solution:</strong> Ensure PrimeNG module/component is imported</li>
      <li>Check if PrimeNG styles are included in <code>angular.json</code></li>
      <li>Verify component is properly declared in standalone component imports</li>
    </ul>

    <h3>Performance Issues</h3>
    <h4>Slow build times</h4>
    <ul>
      <li><strong>Solution:</strong> Use <code>ng build --watch</code> for incremental builds</li>
      <li>Consider using Angular's build cache</li>
      <li>Check for large dependencies that could be optimized</li>
    </ul>

    <h4>Large bundle size</h4>
    <ul>
      <li><strong>Solution:</strong> This app does not use lazy loading; consider it for large features if the app grows. Otherwise: remove unused dependencies, use tree-shaking, and analyze with <code>ng build --stats-json</code> and webpack-bundle-analyzer</li>
    </ul>

    <h3>Known Issues</h3>
    <div class="info">
      <strong>Note:</strong> If you encounter issues not listed here:
      <ul>
        <li>Check Angular version compatibility</li>
        <li>Review browser console for detailed error messages</li>
        <li>Check network tab for API errors</li>
        <li>Verify environment configuration</li>
        <li>Consult Angular documentation: <a href="https://angular.io/docs" target="_blank">angular.io/docs</a></li>
      </ul>
    </div>
  </div>

  <!-- Footer -->
  <div class="section" style="margin-top: 3rem; text-align: center; color: #777;">
    <p><strong>Car Wash Management System</strong></p>
    <p>Documentation last updated: {{ currentDate }}</p>
    <p>For questions or issues, please contact the development team.</p>
  </div>
</div>

